import { useEffect, useState, useCallback, useRef } from 'react';
import type { Timer, Alarm } from '@/types/clock';
import { clockSounds } from '@/utils/clockSounds';

interface TriggerEvent {
  type: 'timer' | 'alarm';
  id: string;
  label?: string;
}

export const useTimerAlarmMonitor = (
  timers: Timer[],
  alarms: Alarm[],
  onTimerComplete: (id: string) => void,
  onAlarmTriggered: (id: string) => void
) => {
  const [activeTrigger, setActiveTrigger] = useState<TriggerEvent | null>(null);
  const checkedTimersRef = useRef<Set<string>>(new Set());
  const checkedAlarmsRef = useRef<Set<string>>(new Set());

  // Monitor timers
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      
      timers.forEach((timer) => {
        if (!timer.running) return;
        if (checkedTimersRef.current.has(timer.id)) return;
        
        const remaining = timer.targetTs - now;
        
        if (remaining <= 0) {
          // Timer completed!
          checkedTimersRef.current.add(timer.id);
          setActiveTrigger({
            type: 'timer',
            id: timer.id,
            label: timer.label
          });
          
          // Request browser notification permission if not granted
          if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
          }
        }
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timers]);

  // Monitor alarms
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      
      alarms.forEach((alarm) => {
        if (!alarm.enabled) return;
        if (checkedAlarmsRef.current.has(alarm.id)) return;
        
        const diff = alarm.nextTriggerTs - now;
        
        // Trigger within 1 second window
        if (diff <= 1000 && diff >= -1000) {
          checkedAlarmsRef.current.add(alarm.id);
          setActiveTrigger({
            type: 'alarm',
            id: alarm.id,
            label: alarm.label
          });
          
          // Request notification permission
          if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
          }
        }
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [alarms]);

  // Cleanup checked refs when items are removed
  useEffect(() => {
    const timerIds = new Set(timers.map(t => t.id));
    checkedTimersRef.current.forEach(id => {
      if (!timerIds.has(id)) {
        checkedTimersRef.current.delete(id);
      }
    });
  }, [timers]);

  useEffect(() => {
    const alarmIds = new Set(alarms.map(a => a.id));
    checkedAlarmsRef.current.forEach(id => {
      if (!alarmIds.has(id)) {
        checkedAlarmsRef.current.delete(id);
      }
    });
  }, [alarms]);

  const handleDismiss = useCallback(() => {
    if (!activeTrigger) return;
    
    if (activeTrigger.type === 'timer') {
      onTimerComplete(activeTrigger.id);
    } else {
      onAlarmTriggered(activeTrigger.id);
    }
    
    setActiveTrigger(null);
  }, [activeTrigger, onTimerComplete, onAlarmTriggered]);

  const handleSnooze = useCallback(() => {
    if (!activeTrigger || activeTrigger.type !== 'alarm') return;
    
    // Snooze logic handled by parent (add 5 minutes to alarm)
    onAlarmTriggered(activeTrigger.id);
    setActiveTrigger(null);
  }, [activeTrigger, onAlarmTriggered]);

  return {
    activeTrigger,
    handleDismiss,
    handleSnooze
  };
};
